import Add16 from '@carbon/icons-react/es/add/16';
import Close16 from '@carbon/icons-react/es/close/16';
import { Table, Tooltip } from 'antd';
import moment from 'moment';
import React, { FC, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import useDeepCompareEffect from 'use-deep-compare-effect';
import { ProjectId, Squad } from 'api/models';
import { DependentTask } from 'api/tasks';
import { DueDate } from 'components/atoms/due-date';
import { SquadAvatarList } from 'components/atoms/squad-avatar-list';
import { AddDependentTask } from 'components/molecules/tasks/details/dependent-tasks/add-dependent-task';
import styles from 'components/molecules/tasks/details/dependent-tasks/dependent-tasks.module.css';
import { Empty } from 'components/molecules/tasks/details/empty';
import { RbacButton } from 'components/organisms/rbac/components/rbac-buttons';
import { HelperText01, Text03 } from 'components/styles';
import { useProjectDetail } from 'data/projects';
import { ProjectResource } from 'hooks/rbac/useWithAccess';
import {
  HideIfNotAllowed,
  SwitchContentBasedOnAccess,
} from 'hooks/rbac/withAccess';
import { useTranslation } from 'hooks/use-translation';
import { useGetDependenciesMap } from 'queries/project';
import { TaskNameColumnRenderer } from './table';

interface Props {
  taskId: number;
  data?: Array<DependentTask>;

  onFetchTasks(): Promise<DependentTask[]>;

  onCreateDependency(task: DependentTask): void;

  onRemoveDependency(task: DependentTask): void;

  onTaskClick?(task: DependentTask): void;

  projectId: number;
}

export const EmptyMessage = styled.span`
  ${HelperText01};
  ${Text03};
`;

export const DependentTasksList: FC<Props> = (props) => {
  const {
    data = [],
    taskId,
    onFetchTasks,
    onCreateDependency,
    onRemoveDependency,
    projectId,
  } = props;

  const [records, setRecords] = useState<DependentTask[]>(data);

  const { data: projectDetail } = useProjectDetail(ProjectId(projectId));

  const projectResource = useMemo(
    () => (projectDetail ? ProjectResource(projectDetail) : undefined),
    [projectDetail],
  );

  useDeepCompareEffect(() => {
    setRecords(data);
  }, [data]);

  const handleRemoveDependentTask = (_task: DependentTask) => {
    setRecords(records.filter((r) => r.taskId !== _task.taskId));

    onRemoveDependency(_task);
  };

  const DEPENDENT_TASKS_COLUMNS = [
    {
      title: useTranslation('task_name'),
      dataIndex: 'taskName',
      key: 'taskName',
      render: (value, record: DependentTask) => (
        <TaskNameColumnRenderer record={record} />
      ),
    },
    {
      title: useTranslation('assigned_to'),
      className: styles.assignee,
      width: '20%',
      render: (value, record: DependentTask) =>
        record.assignee && <SquadAvatarList squad={record.assignee as Squad} />,
    },
    {
      title: useTranslation('due_date'),
      className: styles.due_date,
      width: '20%',
      render: (text, record: DependentTask) =>
        record.dueDate && (
          <DueDate
            timestamp={moment(record.dueDate)}
            completed={Boolean(record.completedAt)}
          />
        ),
    },
    {
      className: styles.more_options,
      width: '32px',
      render: (text, record) => (
        <div className={styles.remove}>
          <Tooltip title={<FormattedMessage id="remove" />} placement="left">
            <RbacButton
              type="text"
              icon={<Close16 />}
              onClick={() => handleRemoveDependentTask(record)}
              rule="TASK_DEPENDENCY:WRITE"
              resourceData={projectResource}
            />
          </Tooltip>
        </div>
      ),
    },
  ];

  const [canShowEmpty, setCanShowEmpty] = useState<boolean>(!records.length);

  const { data: dependenciesMap } = useGetDependenciesMap(projectId);

  useDeepCompareEffect(() => {
    setCanShowEmpty(!records.length);
  }, [records]);

  const handleNewTaskAdd = (task: DependentTask) => {
    setRecords([...records, { ...task }]);

    onCreateDependency(task);
  };

  return !canShowEmpty ? (
    <>
      {/* We need this check here, otherwise ant table will show it's empty state.
            Currently not sure about how to disable that empty state. */}
      {records.length > 0 && (
        <Table
          size="small"
          rowKey="taskId"
          className={styles.table}
          rowClassName={(record: DependentTask) =>
            `${styles.row} ${record.private ? styles['private-row'] : ''}`
          }
          pagination={false}
          columns={DEPENDENT_TASKS_COLUMNS}
          dataSource={records}
        />
      )}
      <HideIfNotAllowed
        rule="TASK_DEPENDENCY:WRITE"
        resourceData={projectResource}
      >
        <AddDependentTask
          taskId={taskId}
          onChange={handleNewTaskAdd}
          existingTasks={records.map((r) => r.taskId)}
          canFocusImmediately={records.length === 0}
          onFetchTasks={onFetchTasks}
          dependenciesMap={dependenciesMap}
        />
      </HideIfNotAllowed>
    </>
  ) : (
    <Empty>
      <SwitchContentBasedOnAccess
        rule="TASK_DEPENDENCY:WRITE"
        resourceData={projectResource}
        hasAccess={
          <RbacButton
            onClick={() => setCanShowEmpty(false)}
            className={styles.add_btn}
            type="link"
            size="small"
            icon={<Add16 />}
            rule="TASK_DEPENDENCY:WRITE"
            resourceData={projectResource}
            propsIfNoAccess={{
              disabled: true,
            }}
            data-cy="add_dependent_tasks.button"
          >
            <FormattedMessage id="blocking_tasks.add" />
          </RbacButton>
        }
        doesNotHaveAccess={
          <EmptyMessage>
            <FormattedMessage id="reports.template_insight.table.empty.task" />
          </EmptyMessage>
        }
      />
    </Empty>
  );
};
